'use client'

import { openLogin } from '@/components/auth/open-auth'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useCreatorCampaigns } from '@/hooks/react-query/community-v2/use-creator-campaigns.query'
import { useFocProfileQuery } from '@/hooks/react-query/community-v2/use-foc-profile.query'
import { useMyCampaignRegistrations } from '@/hooks/react-query/community-v2/use-my-campaign-registrations.query'
import { useIsLoggedIn, useUserQuery } from '@/hooks/react-query/user/use-user'
import { Campaign, KolProfile } from '@/types/campaign'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { mapCreatorCampaignToCampaign } from '../../_data/map-creator-campaign'
import { CampaignCard } from './campaign-card'
import { CampaignKolProfileModal } from './campaign-kol-profile-modal'
import { isKolProfileComplete } from './campaign-kol-profile.util'
import { CampaignRegisterModal } from './campaign-register-modal'

export const CampaignListSection = () => {
  const t = useTranslations('community_threads.campaign.list')
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { data: creatorCampaigns } = useCreatorCampaigns()
  const { data: focProfile, isLoading: isFocProfileLoading } =
    useFocProfileQuery()
  const { data: registeredCampaignIds } = useMyCampaignRegistrations()
  const isLoggedIn = useIsLoggedIn()
  // useUserQuery cùng queryKey với useIsLoggedIn nên không gọi thêm API,
  // chỉ để lấy isLoading - biết khi nào trạng thái đăng nhập đã xác định.
  const { isLoading: isUserLoading } = useUserQuery()
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  )
  const [isProductModalOpen, setIsProductModalOpen] = useState(false)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  // Ghi đè cục bộ ngay sau khi vừa hoàn tất popup thông tin, tránh phải
  // đợi refetch API mới coi là "đã đủ thông tin" cho lượt đăng ký này.
  const [kolProfileOverride, setKolProfileOverride] =
    useState<KolProfile | null>(null)

  const kolProfile = kolProfileOverride ?? focProfile ?? null

  const campaigns = useMemo(
    () => (creatorCampaigns ?? []).map(mapCreatorCampaignToCampaign),
    [creatorCampaigns]
  )

  const registeredIds = useMemo(
    () =>
      new Set((registeredCampaignIds ?? []).map((item) => item.campaign_id)),
    [registeredCampaignIds]
  )

  // Cho phép mở thẳng popup đăng ký campaign qua URL, vd chia sẻ link
  // /community-threads?register_campaign=<slug> - chỉ tự mở 1 lần lúc vào
  // trang, các lần mở popup sau (kể cả tự mở từ URL) đều do handleRegisterClick
  // set lại URL này.
  const autoOpenedRef = useRef(false)

  const handleRegisterClick = (campaign: Campaign) => {
    if (!isLoggedIn) {
      openLogin({ callback: () => handleRegisterClick(campaign) })
      return
    }
    // Đánh dấu đã tự mở (nếu chưa) để effect đọc URL bên dưới không mở lại
    // lần nữa - tránh gọi trùng khi chính effect đó vừa gọi hàm này.
    autoOpenedRef.current = true
    setSelectedCampaign(campaign)
    router.replace(`${pathname}?register_campaign=${campaign.slug}`, {
      scroll: false,
    })
    if (isKolProfileComplete(kolProfile)) {
      setIsProductModalOpen(true)
    } else {
      setIsProfileModalOpen(true)
    }
  }

  const handleProfileComplete = (profile: KolProfile) => {
    setKolProfileOverride(profile)
    setIsProfileModalOpen(false)
    setIsProductModalOpen(true)
  }

  const registerCampaignSlug = searchParams.get('register_campaign')
  useEffect(() => {
    if (!registerCampaignSlug || autoOpenedRef.current) return
    // Đợi xác định xong trạng thái đăng nhập, rồi nếu đã đăng nhập thì đợi
    // luôn hồ sơ KOL tải xong - nếu không, kolProfile vẫn là null lúc effect
    // chạy nên bị coi là "chưa đủ thông tin" và hiện nhầm form đăng ký dù
    // thực ra user đã có đủ thông tin.
    if (isUserLoading) return
    if (isLoggedIn && isFocProfileLoading) return
    const campaign = campaigns.find((c) => c.slug === registerCampaignSlug)
    if (!campaign) return

    handleRegisterClick(campaign)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ cần chạy lại khi các điều kiện chờ đổi, handleRegisterClick tạo lại mỗi render
  }, [
    registerCampaignSlug,
    campaigns,
    isUserLoading,
    isLoggedIn,
    isFocProfileLoading,
  ])

  // Dọn param khỏi URL khi cả 2 popup (hồ sơ KOL + chọn sản phẩm) đều đã
  // đóng - không dọn ngay lúc chuyển từ popup hồ sơ sang popup chọn sản
  // phẩm (lúc đó vẫn còn 1 popup đang mở).
  useEffect(() => {
    if (!selectedCampaign) return
    if (isProfileModalOpen || isProductModalOpen) return
    if (!searchParams.get('register_campaign')) return
    router.replace(pathname, { scroll: false })
  }, [
    selectedCampaign,
    isProfileModalOpen,
    isProductModalOpen,
    pathname,
    router,
    searchParams,
  ])

  if (!campaigns.length) return null

  return (
    <div className='mt-8 md:mt-20'>
      <Carousel
        opts={{
          align: 'start',
          loop: false,
        }}
        className='w-full'
      >
        <div className='mb-4 flex items-end justify-between md:mb-8'>
          <div>
            <h3 className='font-criteria text-2xl font-medium uppercase text-[#111111] md:text-[28px]'>
              {t('title')}
            </h3>
            <p className='mt-2 text-sm text-neutral-600 md:text-base'>
              {t('subtitle')}
            </p>
          </div>
          <div className='hidden gap-3 md:flex'>
            <CarouselPrevious
              hideIfStart
              className='static h-10 w-10 translate-x-0 translate-y-0 rounded-full border-none bg-gray-200 text-black hover:bg-gray-300'
            />
            <CarouselNext
              hideIfEnd
              className='static h-10 w-10 translate-x-0 translate-y-0 rounded-full border-none bg-[#111] text-white hover:bg-black/80'
            />
          </div>
        </div>

        <CarouselContent className='-ml-4'>
          {campaigns.map((campaign) => (
            <CarouselItem
              key={campaign.id}
              className='basis-[83.333%] pl-4 md:basis-[31.25%] lg:basis-1/4 2xl:basis-1/5'
            >
              <CampaignCard
                campaign={campaign}
                isRegistered={registeredIds.has(campaign.id)}
                onRegisterClick={handleRegisterClick}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {selectedCampaign && (
        <>
          <CampaignKolProfileModal
            open={isProfileModalOpen}
            onOpenChange={setIsProfileModalOpen}
            onComplete={handleProfileComplete}
          />
          {isKolProfileComplete(kolProfile) && (
            <CampaignRegisterModal
              campaign={selectedCampaign}
              kolProfile={kolProfile}
              open={isProductModalOpen}
              onOpenChange={setIsProductModalOpen}
            />
          )}
        </>
      )}
    </div>
  )
}
